<template>
  <div class="home-content">
    <!-- Hero Section -->
    <section class="hero-section">
      <div class="container">
        <div class="row align-items-center">
          <div class="col-lg-6 hero-text-column">
            <h1 class="hero-title">FoodCycle.ai</h1>
            <h2 class="hero-subtitle">Redefining the foodcycle</h2>
            <p class="hero-description">
              Transform food surplus into sustainability opportunities. Our AI-supported decision support tool helps your business 
              reduce waste, find innovative solutions, and make a positive environmental impact.
            </p>
            <div class="hero-cta">
              <router-link to="/tool" class="btn btn-primary btn-lg me-3">
                Start Now
              </router-link>
              <router-link to="/tool-description" class="btn btn-outline-secondary btn-lg">
                Learn More
              </router-link>
            </div>
          </div>
          <div class="col-lg-6 logos-column">
            <div class="logos-container">
              <!-- CEFoodCycle Logo -->
              <div class="logo-wrapper fh-logo mb-4">
                <ImageWithFallback 
                  :src="require('@/assets/logos/FH_CEFoodCycle_Logo.png')" 
                  :fallback-src="'/dev/images/logos/FH_CEFoodCycle_Logo.png'"
                  alt="FH CEFoodCycle Logo" 
                  img-class="img-fluid"
                  loading="eager"
                />
              </div>
              
              <!-- Interreg Logo -->
              <div class="logo-wrapper interreg-logo">
                <ImageWithFallback 
                  :src="require('@/assets/logos/CEFoodCycle_Interreg.png')" 
                  :fallback-src="'/dev/images/logos/CEFoodCycle_Interreg.png'"
                  alt="CEFoodCycle Interreg Logo" 
                  img-class="img-fluid"
                  loading="eager"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Features Section -->
    <section class="features-section">
      <div class="container">
        <h2 class="section-title text-center">How FoodCycle.ai Works</h2>
        
        <div class="row g-4 mt-3">
          <!-- Feature 1 -->
          <div class="col-md-4">
            <div class="feature-card">
              <div class="feature-icon">
                <i class="bi bi-search"></i>
              </div>
              <h3 class="feature-title">Analyze Surplus</h3>
              <p class="feature-description">
                Log and track your food surplus with our easy-to-use dashboard.
              </p>
            </div>
          </div>
          
          <!-- Feature 2 -->
          <div class="col-md-4">
            <div class="feature-card">
              <div class="feature-icon">
                <i class="bi bi-lightbulb"></i>
              </div>
              <h3 class="feature-title">Discover Solutions</h3>
              <p class="feature-description">
                Get tailored suggestions for repurposing surplus into valuable resources.
              </p>
            </div>
          </div>
          
          <!-- Feature 3 -->
          <div class="col-md-4">
            <div class="feature-card">
              <div class="feature-icon">
                <i class="bi bi-bar-chart"></i>
              </div>
              <h3 class="feature-title">Measure Impact</h3>
              <p class="feature-description">
                Track environmental benefits through our lifecycle assessment tools.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- About Project Section -->
    <section class="about-section">
      <div class="container">
        <div class="about-content">
          <h2 class="section-title">About FoodCycle.ai</h2>
          <p class="about-description">
            Welcome to FoodCycle.ai, a tool developed as part of the CEFoodCycle project to help companies manage food surplus and waste. 
            Our decision support tool suggests practical solutions such as upcycling food waste, redirecting to food banks, creating non-food products, 
            or biogas production. We connect you with solution providers while providing environmental impact assessments of both your surplus 
            and the proposed solutions.
          </p>
          <p class="about-description">
            Our integrated chatbot assists you throughout the entire process, answering your questions and providing support when you need it most.
          </p>
          <div class="text-center mt-4">
            <router-link to="/tool" class="btn btn-primary btn-lg">
              Start FoodCycle.ai
            </router-link>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import ImageWithFallback from '@/components/ImageWithFallback.vue';

export default {
  name: 'HomeContent',
  components: {
    ImageWithFallback
  }
};
</script>

<style scoped>
/* General Styles */
.home-content {
  width: 100%;
  overflow-x: hidden;
}

.section-title {
  color: #333;
  font-weight: 700;
  margin-bottom: 1.5rem;
}

.btn-primary {
  background-color: #87b140;
  border-color: #87b140;
  transition: all 0.3s ease;
}

.btn-primary:hover {
  background-color: #75a236;
  border-color: #75a236;
}

.btn-outline-secondary {
  border-color: #555;
  color: #555;
  transition: all 0.3s ease;
}

.btn-outline-secondary:hover {
  background-color: #555;
  color: white;
}

/* Hero Section */
.hero-section {
  padding: 5rem 0;
  background-color: #f2f5e7;
  position: relative;
  width: 100%;
}

.hero-title {
  font-size: 3.5rem;
  font-weight: 800;
  color: #333;
  margin-bottom: 0.5rem;
}

.hero-subtitle {
  font-size: 1.8rem;
  font-weight: 600;
  color: #4a4a4a;
  margin-bottom: 1.5rem;
}

.hero-description {
  font-size: 1.1rem;
  line-height: 1.6;
  color: #555;
  margin-bottom: 2rem;
}

.hero-cta {
  margin-top: 2rem;
}

/* Logos Column Styles */
.logos-column {
  display: flex;
  justify-content: center;
  align-items: center;
}

.logos-container {
  background-color: white;
  padding: 2.5rem;
  border-radius: 10px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
  width: 100%;
  max-width: 500px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.logo-wrapper {
  text-align: center;
  width: 100%;
  transition: all 0.3s ease;
}

.logo-wrapper img {
  max-width: 100%;
  height: auto;
}

.fh-logo {
  margin-bottom: 2rem;
  padding-bottom: 2rem;
  border-bottom: 1px solid #eee;
}

.interreg-logo {
  padding-top: 0.5rem;
}

/* Features Section */
.features-section {
  padding: 5rem 0;
  background-color: white;
  width: 100%;
}

.feature-card {
  background-color: white;
  border-radius: 10px;
  padding: 2rem;
  height: 100%;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  text-align: center;
}

.feature-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
}

.feature-icon {
  background-color: #f2f5e7;
  color: #87b140;
  width: 70px;
  height: 70px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  margin: 0 auto 1.5rem;
}

.feature-icon i {
  font-size: 2rem;
}

.feature-title {
  font-size: 1.4rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 1rem;
}

.feature-description {
  color: #666;
  line-height: 1.6;
}

/* About Section */
.about-section {
  padding: 5rem 0;
  background-color: #f8f9fa;
  width: 100%;
}

.about-content {
  max-width: 900px;
  margin: 0 auto;
  text-align: center;
}

.about-description {
  font-size: 1.1rem;
  line-height: 1.7;
  color: #555;
  margin-bottom: 1.5rem;
}

/* Responsive Styles */
@media (max-width: 992px) {
  .hero-section {
    padding: 4rem 0;
  }
  
  .hero-title {
    font-size: 2.8rem;
  }

  .hero-subtitle {
    font-size: 1.5rem;
  }
  
  .logos-column {
    margin-top: 3rem;
  }
  
  .logos-container {
    padding: 2rem;
  }
}

@media (max-width: 768px) {
  .hero-section, .features-section, .about-section {
    padding: 3rem 0;
  }
  
  .hero-title {
    font-size: 2.5rem;
  }
  
  .hero-text-column {
    text-align: center;
  }
  
  .hero-cta .btn {
    display: block;
    width: 100%;
    margin-right: 0 !important;
    margin-bottom: 1rem;
  }
  
  .hero-cta .btn:last-child {
    margin-bottom: 0;
  }
  
  .logos-container {
    padding: 1.5rem;
  }
  
  .fh-logo {
    margin-bottom: 1.5rem;
    padding-bottom: 1.5rem;
  }
}

@media (max-width: 576px) {
  .hero-section {
    padding-top: 2rem;
  }
  
  .hero-title {
    font-size: 2.2rem;
  }
  
  .hero-subtitle {
    font-size: 1.3rem;
  }
}
</style>